import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { DollarSign, Clock, CheckCircle, XCircle, AlertTriangle, Eye, CreditCard, Banknote, RefreshCw, X } from 'lucide-react';

const Payouts = () => {
  const { colors } = useTheme();
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transactionRef, setTransactionRef] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'payoutRequests'),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const requests = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          let hostName = 'Unknown';
          let hostEmail = '';
          let hostPhone = '';
          let hostCity = '';
          let hostAddress = '';
          let hostAvatar = null;
          
          try {
            const hostDoc = await getDoc(doc(db, 'users', data.hostId));
            if (hostDoc.exists()) {
              const hostData = hostDoc.data();
              hostName = hostData.name || hostData.displayName || hostData.email || 'Unknown';
              hostEmail = hostData.email || '';
              hostPhone = hostData.phone || hostData.phoneNumber || '';
              hostCity = hostData.city || '';
              hostAddress = hostData.address || '';
              hostAvatar = hostData.profileImageUrl || null;
            }
          } catch (error) {
            console.error('Error fetching host:', error);
          }

          return {
            id: docSnap.id,
            ...data,
            hostName,
            hostEmail,
            hostPhone,
            hostCity,
            hostAddress,
            hostAvatar,
            requestedAt: data.requestedAt?.toDate(),
            processedAt: data.processedAt?.toDate(),
            completedAt: data.completedAt?.toDate(),
          };
        })
      );
      setPayoutRequests(requests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusUpdate = async (requestId, newStatus, additionalData = {}) => {
    setProcessing(true);
    try {
      const request = payoutRequests.find(r => r.id === requestId);
      if (!request) {
        toast.error('Request not found');
        setProcessing(false);
        return;
      }

      if (newStatus === 'processing') {
        try {
          const createMoyasarPayout = httpsCallable(functions, 'createMoyasarPayout');
          const callableResult = await createMoyasarPayout({ requestId });
          const moyasarPayout = callableResult.data || {};

          additionalData.moyasarPayoutId = moyasarPayout.payoutId;
          additionalData.moyasarStatus = moyasarPayout.status;
          additionalData.moyasarSequenceNumber = moyasarPayout.sequenceNumber;
          additionalData.transactionReference = moyasarPayout.sequenceNumber;

          toast.success('Moyasar payout created! Webhook will sync status automatically.');
        } catch (moyasarError) {
          console.error('Moyasar error:', moyasarError);
          toast.error(`Failed to create Moyasar payout: ${moyasarError.message}`);
          setProcessing(false);
          return;
        }
      }

      const updateData = {
        status: newStatus,
        ...additionalData,
      };

      if (newStatus === 'processing') {
        updateData.processedAt = serverTimestamp();
      } else if (newStatus === 'completed') {
        updateData.completedAt = serverTimestamp();
      } else if (newStatus === 'failed' || newStatus === 'cancelled') {
        updateData.processedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'payoutRequests', requestId), updateData);
      
      toast.success(`Payout successfully marked as ${newStatus}`);
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error updating payout request:', error);
      toast.error('Failed to update payout request');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualTransfer = async (requestId, additionalData = {}) => {
    setProcessing(true);
    try {
      const request = payoutRequests.find(r => r.id === requestId);
      if (!request) {
        toast.error('Request not found');
        return;
      }

      const tRef = additionalData.transactionReference || window.prompt('Enter manual transfer reference:')?.trim();
      if (!tRef) {
        toast.error('Transfer reference is required');
        return;
      }

      const updateData = {
        status: 'completed',
        completedAt: serverTimestamp(),
        processedAt: serverTimestamp(),
        processedBy: 'admin_manual_transfer',
        manualProcessed: true,
        manuallyProcessed: true,
        transactionReference: tRef,
        ...additionalData,
      };

      await updateDoc(doc(db, 'payoutRequests', requestId), updateData);

      toast.success('Manual transfer logged successfully');
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error completing manual payout:', error);
      toast.error('Failed to save manual transfer');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, text: 'Pending', icon: Clock },
      processing: { bg: colors.primaryGhost, color: colors.primary, text: 'Processing', icon: RefreshCw },
      completed: { bg: colors.successGhost, color: colors.success, text: 'Completed', icon: CheckCircle },
      failed: { bg: colors.errorGhost, color: colors.error, text: 'Failed', icon: XCircle },
      cancelled: { bg: colors.backgroundSecondary, color: colors.textSecondary, text: 'Cancelled', icon: AlertTriangle }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </span>
    );
  };

  const formatCurrencyLocal = (amount) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
    }).format(amount || 0);
  };

  const formatDateLocal = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatIban = (iban) => {
    if (!iban) return '';
    const cleaned = iban.replace(/\s/g, '');
    return `${cleaned.slice(0, 4)} **** **** ${cleaned.slice(-4)}`;
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setTransactionRef('');
    setNotes(request.notes || '');
    setShowDetailsModal(true);
  };

  const stats = {
    total: payoutRequests.length,
    pending: payoutRequests.filter(r => r.status === 'pending').length,
    processing: payoutRequests.filter(r => r.status === 'processing').length,
    completed: payoutRequests.filter(r => r.status === 'completed').length,
    failed: payoutRequests.filter(r => r.status === 'failed').length,
    cancelled: payoutRequests.filter(r => r.status === 'cancelled').length,
  };

  const visiblePayoutRequests = filter === 'all'
    ? payoutRequests
    : payoutRequests.filter((request) => request.status === filter);

  const kpis = [
    { id: 'pending', label: 'Pending Requests', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'processing', label: 'Processing', value: stats.processing, icon: RefreshCw, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'completed', label: 'Completed', value: stats.completed, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'failed', label: 'Failed', value: stats.failed, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
    { id: 'all', label: 'Total Volume', value: stats.total, icon: DollarSign, color: colors.textSecondary, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Host Payouts
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review, process, and track host earnings disbursements.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filter === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilter(kpi.id)}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div 
                    className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-[18px] h-[18px] text-white" />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'processing', 'completed', 'failed', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-xl text-[13px] font-bold uppercase tracking-wider transition-all shadow-sm ${filter === status ? 'hover:opacity-90' : 'hover:scale-105'}`}
            style={{
              backgroundColor: filter === status ? colors.primary : colors.surface,
              color: filter === status ? 'white' : colors.textSecondary,
              border: `1px solid ${filter === status ? colors.primary : colors.border}`,
            }}
          >
             {status}
          </button>
        ))}
      </div>

      {/* Enhanced Payout Requests Table */}
      <div className="card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
           <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
              <p className="mt-4 text-sm font-medium" style={{ color: colors.textSecondary }}>Loading payouts...</p>
           </div>
        ) : visiblePayoutRequests.length === 0 ? (
           <div className="flex flex-col items-center justify-center p-16">
             <div className="w-16 h-16 rounded-3xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
                <Banknote className="w-8 h-8" style={{ color: colors.textMuted }} />
             </div>
             <h3 className="text-[16px] font-semibold" style={{ color: colors.text }}>No payouts found</h3>
             <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
                No payout requests matching your filters.
             </p>
           </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Host Info</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Payout Amount</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Bank Details</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Requested</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visiblePayoutRequests.map((request, index) => (
                  <tr 
                    key={request.id} 
                    className="group transition-colors cursor-pointer hover:bg-opacity-80"
                    style={{ borderBottom: index < visiblePayoutRequests.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                    onClick={() => openModal(request)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {request.hostAvatar ? (
                          <img className="h-10 w-10 text-[10px] rounded-full object-cover border-2" style={{ borderColor: colors.border }} src={request.hostAvatar} alt="Avatar" />
                        ) : (
                          <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                            {request.hostName?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-[14px] font-bold truncate" style={{ color: colors.text }}>{request.hostName}</div>
                          <div className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>{request.hostEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <span className="text-[16px] font-black" style={{ color: colors.primary }}>{formatCurrencyLocal(request.amount)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="w-3.5 h-3.5" style={{ color: colors.textMuted }}/>
                          <span className="text-[13px] font-bold" style={{ color: colors.text }}>{request.bankName || 'Unknown Bank'}</span>
                       </div>
                       <div className="text-[11px] font-bold tracking-wider uppercase" style={{ color: colors.textSecondary }}>{formatIban(request.iban)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[12px] font-medium" style={{ color: colors.textSecondary }}>
                      {formatDateLocal(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                       <button
                         className="px-3 py-2 rounded-xl text-[12px] font-bold transition-all shadow-sm hover:scale-105 inline-flex items-center gap-1.5"
                         style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}
                         onClick={(e) => { e.stopPropagation(); openModal(request); }}
                       >
                          <Eye className="w-3.5 h-3.5" /> View
                       </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="admin-modal-overlay fixed inset-0 z-[70] p-4 sm:p-6 overflow-y-auto animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
          <div className="min-h-full flex items-center justify-center">
            <div className="admin-modal-card admin-modal-scroll rounded-3xl max-w-2xl w-full p-5 sm:p-8 shadow-2xl relative animate-fadeInScale overflow-hidden" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6">
              <div className="flex items-center gap-4 min-w-0">
                 <div className="w-14 h-14 rounded-2xl flex flex-col justify-center items-center shadow-lg bg-gradient-to-br from-green-500 to-emerald-600 text-white border border-white/20">
                    <Banknote className="w-6 h-6" />
                 </div>
                 <div>
                    <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: colors.text }}>Payout Authorization</h3>
                    <p className="text-[13px] font-bold font-mono tracking-wider opacity-60 mt-1" style={{ color: colors.textSecondary }}>REF: {selectedRequest.id}</p>
                 </div>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="p-2.5 rounded-full hover:rotate-90 transition-all duration-300" style={{ backgroundColor: colors.backgroundSecondary }}>
                <X className="w-5 h-5" style={{ color: colors.text }} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
               <div className="p-4 rounded-2xl border flex flex-col justify-center" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Disbursement Amount</p>
                  <p className="text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-emerald-500 to-green-600">{formatCurrencyLocal(selectedRequest.amount)}</p>
               </div>
               <div className="p-4 rounded-2xl border flex flex-col justify-center" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest mb-2" style={{ color: colors.textMuted }}>Current Status</p>
                  <div>{getStatusBadge(selectedRequest.status)}</div>
               </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px mb-6 border rounded-2xl overflow-hidden" style={{ borderColor: colors.border, backgroundColor: colors.border }}>
                <div className="p-4" style={{ backgroundColor: colors.surface }}>
                   <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Host Identity</p>
                   <p className="text-[14px] font-bold" style={{ color: colors.text }}>{selectedRequest.hostName}</p>
                   <p className="text-[12px]" style={{ color: colors.textSecondary }}>{selectedRequest.hostEmail}</p>
                </div>
                <div className="p-4" style={{ backgroundColor: colors.surface }}>
                   <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Funding Source Info</p>
                   <p className="text-[14px] font-bold" style={{ color: colors.text }}>{selectedRequest.bankName}</p>
                   <p className="text-[12px] font-bold font-mono mt-0.5" style={{ color: colors.textSecondary }}>IBAN: <span className="opacity-70">{formatIban(selectedRequest.iban)}</span></p>
                   <p className="text-[11px] uppercase tracking-wide mt-0.5" style={{ color: colors.textSecondary }}>To: {selectedRequest.accountHolderName}</p>
                </div>
            </div>

             <div className="flex flex-wrap gap-4 mb-6 text-[12px] font-medium" style={{ color: colors.textSecondary }}>
                <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> Req: {formatDateLocal(selectedRequest.requestedAt)}</div>
                {selectedRequest.processedAt && <div className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5"/> Proc: {formatDateLocal(selectedRequest.processedAt)}</div>}
                {selectedRequest.completedAt && <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" style={{ color: colors.success }}/> Fin: {formatDateLocal(selectedRequest.completedAt)}</div>}
             </div>

            {selectedRequest.failureReason && (
               <div className="p-4 rounded-xl mb-6 flex gap-3 items-start" style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}30`, color: colors.error }}>
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-[13px] uppercase tracking-wider">Failure Reason</h4>
                    <p className="mt-1 text-[13px] font-medium leading-relaxed">{selectedRequest.failureReason}</p>
                  </div>
               </div>
            )}

            {selectedRequest.transactionReference && (
              <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: colors.backgroundSecondary, border: `1px dashed ${colors.border}` }}>
                  <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textSecondary }}>Transaction Reference</p>
                  <p className="font-mono text-[14px] font-bold" style={{ color: colors.text }}>{selectedRequest.transactionReference}</p>
              </div>
            )}

            {selectedRequest.notes && (
                <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}` }}>
                    <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textSecondary }}>Admin Notes</p>
                    <p className="text-[13px] leading-relaxed" style={{ color: colors.text }}>{selectedRequest.notes}</p>
                </div>
            )}

            {selectedRequest.status === 'pending' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t" style={{ borderColor: colors.border }}>
                <button
                  onClick={() => handleStatusUpdate(selectedRequest.id, 'processing')}
                  disabled={processing}
                  className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}
                >
                  Moyasar Payout
                </button>
                <button
                  onClick={() => handleManualTransfer(selectedRequest.id)}
                  disabled={processing}
                  className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                >
                  Manual Log
                </button>
                <button
                  onClick={() => {
                    const reason = prompt('Enter rejection reason:');
                    if (reason && reason.trim()) {
                      handleStatusUpdate(selectedRequest.id, 'failed', { failureReason: reason.trim() });
                    }
                  }}
                  disabled={processing}
                  className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                >
                  Reject Payout
                </button>
              </div>
            )}

            {selectedRequest.status === 'processing' && (
              <div className="pt-4 border-t" style={{ borderColor: colors.border }}>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Manual TXN Ref (Optional)</label>
                        <input
                           type="text"
                           value={transactionRef}
                           onChange={(e) => setTransactionRef(e.target.value)}
                           className="w-full p-3 rounded-xl text-[14px] focus:outline-none placeholder-opacity-50"
                           placeholder="Enter TXN-ID"
                           style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                           onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                           onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Internal Notes</label>
                        <input
                           type="text"
                           value={notes}
                           onChange={(e) => setNotes(e.target.value)}
                           className="w-full p-3 rounded-xl text-[14px] focus:outline-none placeholder-opacity-50"
                           placeholder="Add resolution details..."
                           style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                           onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                           onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>
                 </div>
                 
                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                   <button
                     onClick={() => {
                       handleStatusUpdate(selectedRequest.id, 'completed', {
                         transactionReference: transactionRef || null,
                         notes: notes || null,
                       });
                     }}
                     disabled={processing}
                     className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                     style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                   >
                     Force Complete
                   </button>
                   <button
                     onClick={() => {
                       const reason = prompt('Enter failure reason:');
                       if (reason) {
                         handleStatusUpdate(selectedRequest.id, 'failed', {
                           failureReason: reason,
                           notes: notes || null,
                         });
                       }
                     }}
                     disabled={processing}
                     className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                     style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                   >
                     Mark Failed
                   </button>
                   <button
                     onClick={() => {
                       if (window.confirm('Are you sure you want to cancel this payout? Host available balance will NOT be automatically reverted via this manual override.')) {
                         handleStatusUpdate(selectedRequest.id, 'cancelled', { notes: notes || null });
                       }
                     }}
                     disabled={processing}
                     className="py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                     style={{ background: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' }}
                   >
                     Cancel Link
                   </button>
                 </div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Payouts;
